import React from 'react';
import { useAuth } from '@clerk/clerk-react';
import {
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
  type HubConnection,
} from '@microsoft/signalr';
import type { ConnectionStatus } from './use-signalr';

const ONBOARDING_HUB_PATH = '/bff/hubs/onboarding';

export interface OrganizationActivatedPayload {
  orgId: string;
  orgName: string;
}

export interface OrganizationRejectedPayload {
  orgId: string;
  orgName: string;
  reason: string;
}

interface UseOrgApprovalOptions {
  enabled?: boolean;
  onActivated?: (payload: OrganizationActivatedPayload) => void;
  onRejected?: (payload: OrganizationRejectedPayload) => void;
}

interface UseOrgApprovalResult {
  connection: HubConnection | null;
  status: ConnectionStatus;
}

/**
 * Listens on the user-scoped onboarding SignalR hub for the user's pending
 * organization being activated or rejected. Unlike useSignalR, this does NOT
 * depend on a current org — the connection authenticates with the Clerk JWT
 * only, and the server groups the connection by the user's auth subject.
 */
export function useOrgApproval({
  enabled = true,
  onActivated,
  onRejected,
}: UseOrgApprovalOptions = {}): UseOrgApprovalResult {
  const { getToken } = useAuth();
  const [status, setStatus] = React.useState<ConnectionStatus>('disconnected');
  const connectionRef = React.useRef<HubConnection | null>(null);
  const [connection, setConnection] = React.useState<HubConnection | null>(null);

  // Keep latest callbacks in refs so we don't re-create the connection when
  // the caller passes new closures on every render.
  const getTokenRef = React.useRef(getToken);
  getTokenRef.current = getToken;
  const onActivatedRef = React.useRef(onActivated);
  onActivatedRef.current = onActivated;
  const onRejectedRef = React.useRef(onRejected);
  onRejectedRef.current = onRejected;

  React.useEffect(() => {
    if (!enabled) {
      setStatus('disconnected');
      return;
    }

    const bffBaseUrl =
      (typeof import.meta !== 'undefined' && (import.meta.env?.VITE_BFF_BASE_URL as string)) || '';
    const hubUrl = `${bffBaseUrl.replace(/\/$/, '')}${ONBOARDING_HUB_PATH}`;

    const conn = new HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: async () => {
          const token = await (getTokenRef.current?.() ?? Promise.resolve(null));
          return token ?? '';
        },
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(LogLevel.Warning)
      .build();

    connectionRef.current = conn;
    setConnection(conn);

    conn.on('OrganizationActivated', (payload: OrganizationActivatedPayload) => {
      onActivatedRef.current?.(payload);
    });
    conn.on('OrganizationRejected', (payload: OrganizationRejectedPayload) => {
      onRejectedRef.current?.(payload);
    });

    conn.onreconnecting(() => setStatus('reconnecting'));
    conn.onreconnected(() => setStatus('connected'));
    conn.onclose(() => setStatus('disconnected'));

    let cancelled = false;
    setStatus('connecting');
    conn
      .start()
      .then(() => {
        if (!cancelled) setStatus('connected');
      })
      .catch(() => {
        if (!cancelled) setStatus('disconnected');
      });

    return () => {
      cancelled = true;
      conn.off('OrganizationActivated');
      conn.off('OrganizationRejected');
      connectionRef.current = null;
      setConnection(null);
      // stop() is safe to call regardless of state; it resolves once any
      // in-flight start has settled, which keeps StrictMode double-mount clean.
      if (conn.state !== HubConnectionState.Disconnected) {
        conn.stop().catch(() => {});
      }
    };
  }, [enabled]);

  return { connection, status };
}
